import { Button } from "@resource/atlas/button/Button";
import { ButtonGroup } from "@resource/atlas/button/ButtonGroup";
import type { AtlasMenuContentItem } from "@resource/atlas/menu/types";
import { Table } from "@resource/atlas/table";
import {
  getColumnSortHeadCellProps,
  getRowInteractionRowProps,
  getSelectionColumn,
} from "@resource/atlas/table/utils";
import { useLogEvent } from "analytics";
import { UsersSelect } from "client/components/generic/select/UserSelect";
import FakeMultiSelect from "components/FakeMultiSelect";
import { Pagination } from "components/Pagination/Pagination";
import GlobalFilter from "components/Table/GlobalFilter";
import usePagination from "components/Table/hooks/usePagination";
import useQueryParamsState from "components/Table/hooks/useQueryParamState";
import useRowSelect from "components/Table/hooks/useRowSelect";
import NoTableResults from "components/Table/NoTableResults";
import {
  GetJobsQuery,
  InterviewerForSelectionFragment,
  OrderByArg,
} from "generated/graphql-codegen/graphql";
import _ from "lodash";
import router from "next/router";
import { useEffect, useMemo, useState } from "react";
import {
  Column,
  useFilters,
  useFlexLayout,
  useGlobalFilter,
  useResizeColumns,
  useSortBy,
  useTable,
  UseTableCellProps,
} from "react-table";
import { JobStatusFilter } from "shared/jobs/types";

import BulkActionBar from "../BulkActionBar/BulkActionBar";
import SelectedFilters from "../Table/SelectedFilters";
import GuideTemplateAssignmentAction from "./GuideTemplateAssignmentAction";
import JobNameCell from "./JobNameCell";
import { JobStatusSelect } from "./JobStatusSelect";

type Job = GetJobsQuery["openJobs"][0];

type FilterOption = {
  id: string;
  label: string;
};

interface InitialVariables {
  limit: number;
  offset: number;
  search: string;
  atsDepartmentIds: string[];
  atsOfficeIds: string[];
  userMembershipIds: string[];
  orderBy: { [x: string]: string }[];
}

interface RefetchIds {
  search: string;
}

interface JobsTableProps {
  jobs: Job[];
  allJobIds: string[];
  refetchIds(refetchIds?: RefetchIds): Promise<void>;
  totalJobsCount?: number | null;
  loading: boolean;
  initialVariables: InitialVariables;
  offices: FilterOption[];
  departments: FilterOption[];
  selectedUserMemberships: InterviewerForSelectionFragment[];
  filtersLoading: boolean;
}

function JobsTable({
  jobs,
  allJobIds,
  refetchIds,
  totalJobsCount,
  loading,
  initialVariables,
  offices,
  departments,
  selectedUserMemberships,
  filtersLoading,
}: JobsTableProps) {
  const logEvent = useLogEvent({ component: "JobsTable" });
  const columns = useMemo<Column<Job>[]>(
    () => [
      getSelectionColumn(),
      {
        Header: "Title",
        id: "name",
        width: 280,
        accessor: ({ name }: Job) => name,
        Cell: ({ row, value }: UseTableCellProps<Job>) => (
          <JobNameCell job={row.original} value={value} />
        ),
      },
      {
        id: "department",
        width: 0,
        hidden: true,
      },
      {
        id: "office",
        width: 0,
        hidden: true,
      },
      {
        id: "userMembership",
        width: 0,
        hidden: true,
      },
      {
        id: "status",
        width: 0,
        hidden: true,
      },
    ],
    []
  );

  const data = useMemo(() => jobs, [jobs]);
  const allIds = useMemo(() => allJobIds, [allJobIds]);
  const totalCount = useMemo(() => totalJobsCount, [totalJobsCount]);
  const [pageCount, setPageCount] = useState(
    Math.ceil((totalCount ?? 0) / initialVariables.limit)
  );
  const {
    setFilter,
    getTableProps,
    getTableBodyProps,
    toggleAll,
    headerGroups,
    rows,
    setGlobalFilter,
    setPageSize,
    gotoPage,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: tableState,
    setAllFilters,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      allIds,
      totalCount,
      manualGlobalFilter: true,
      autoResetGlobalFilter: true,
      getRowId: ({ id }) => id,
      initialState: {
        hiddenColumns: ["department", "status", "office", "userMembership"],
        globalFilter: initialVariables.search ?? "",
        filters: [
          {
            id: "department",
            value: initialVariables.atsDepartmentIds,
          },
          {
            id: "office",
            value: initialVariables.atsOfficeIds,
          },
          {
            id: "userMembership",
            value: initialVariables.userMembershipIds,
          },
        ],
        pageIndex: initialVariables.offset / initialVariables.limit,
        pageSize: initialVariables.limit,
        sortBy: _(initialVariables.orderBy)
          .flatten()
          .map((value) => ({
            id: _(value).keys().head() as string,
            desc: _(value).values().head() === OrderByArg.desc,
          }))
          .value(),
        isLoading: loading,
      },
      autoResetSelectedRows: false,
      pageCount,
      manualPagination: true,
      autoResetPage: false,
      manualSortBy: true,
      autoResetSortBy: false,
      manualFilters: true,
      autoResetFilters: false,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useQueryParamsState,
    useRowSelect,
    useResizeColumns,
    useFlexLayout
  );
  const { globalFilter, filters, pageSize, pageIndex, selectedRowIds } =
    tableState;
  useEffect(() => {
    setPageCount(Math.ceil((totalCount ?? 0) / pageSize));
  }, [pageSize, totalCount]);

  useEffect(() => {
    if (pageIndex !== 0 && data?.length === 0 && !loading) {
      gotoPage(0);
    }
  }, [pageIndex, gotoPage, data?.length, loading]);

  const selectedIds = _(selectedRowIds).pickBy().keys().value();
  const numSelectedRows = selectedIds.length;

  const selectedOffices = _.find(filters, { id: "office" })?.value;
  const selectedDepartments = _.find(filters, { id: "department" })?.value;
  const selectedStatus =
    _.find(filters, { id: "status" })?.value ?? JobStatusFilter.OPEN;

  const isFiltered =
    selectedUserMemberships.length ||
    selectedOffices ||
    selectedDepartments ||
    selectedStatus !== JobStatusFilter.OPEN ||
    globalFilter !== "";
  const clearFilters = () => {
    setGlobalFilter("");
    setAllFilters(() => [
      {
        id: "userMembership",
        value: [],
      },
      {
        id: "office",
        value: [],
      },
      {
        id: "department",
        value: [],
      },
      {
        id: "status",
        value: JobStatusFilter.OPEN,
      },
    ]);
  };

  const hasResults = !!totalJobsCount;

  useEffect(() => {
    const updateData = async () => {
      if (numSelectedRows) {
        await refetchIds({
          search: globalFilter,
        });
      }
    };
    updateData();
  }, [globalFilter, refetchIds, numSelectedRows]);

  return (
    <div>
      <div className="flex justify-start mt-5 w-full">
        <GlobalFilter
          placeholder="Search by name or Requisition ID"
          className="flex-shrink-0"
          globalFilter={globalFilter ?? ""}
          setGlobalFilter={setGlobalFilter}
        />
        <div className="flex space-x-4 flex-shrink-1 flex-grow px-4 items-center justify-start overflow-hidden">
          <SelectedFilters
            label="Department"
            filterOptions={departments}
            filterValue={selectedDepartments}
            clearFilter={() => setFilter("department", [])}
          />
          <SelectedFilters
            label="Office"
            filterOptions={offices}
            filterValue={selectedOffices}
            clearFilter={() => setFilter("office", [])}
          />
          <SelectedFilters
            label="User"
            filterOptions={selectedUserMemberships.map((u) => ({
              id: u.id,
              label: u.name,
            }))}
            filterValue={selectedUserMemberships.map((u) => u.id)}
            clearFilter={() => setFilter("userMembership", [])}
          />
        </div>
        <ButtonGroup isGhost isDropdown>
          <FakeMultiSelect
            label="Department"
            minWidth="14rem"
            items={useMemo(
              () =>
                departments.map(
                  ({ label, id }): AtlasMenuContentItem => ({
                    _type: "item",
                    children: label,
                    key: id,
                    value: id,
                  })
                ),
              [departments]
            )}
            loading={filtersLoading}
            selectedIds={selectedDepartments}
            onSelectedIdsChange={(val) => setFilter("department", val)}
          />
          <FakeMultiSelect
            label="Office"
            minWidth="14rem"
            items={useMemo(
              () =>
                offices.map(
                  ({ label, id }): AtlasMenuContentItem => ({
                    _type: "item",
                    children: label,
                    key: id,
                    value: id,
                  })
                ),
              [offices]
            )}
            loading={filtersLoading}
            selectedIds={selectedOffices}
            onSelectedIdsChange={(val) => setFilter("office", val)}
          />

          <UsersSelect
            onSelect={(value) => setFilter("userMembership", [value.id])}
            Trigger={
              <Button
                isActive={selectedUserMemberships.length > 0}
                title="Select user"
                className="max-w-[9rem]"
              >
                <p className="flex">
                  <span className="truncate">
                    {selectedUserMemberships.length
                      ? selectedUserMemberships[0].name
                      : "User"}
                  </span>
                </p>
              </Button>
            }
          />
          <div>
            <JobStatusSelect
              value={selectedStatus}
              onChange={(value) => setFilter("status", value)}
            />
          </div>
        </ButtonGroup>
      </div>
      <Table.Root {...getTableProps()} className="mt-4">
        <Table.Head role="rowgroup">
          {_.map(headerGroups, (headerGroup) => (
            <Table.HeadRow {...headerGroup.getHeaderGroupProps()}>
              {_.map(headerGroup.headers, (column) => (
                <Table.HeadCell
                  {...column.getHeaderProps()}
                  {...getColumnSortHeadCellProps(column)}
                >
                  {/* @ts-expect-error - react-table not properly typed in React 18 */}
                  {column.render("Header")}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body {...getTableBodyProps()}>
          {_.map(rows, (row) => {
            prepareRow(row);
            return (
              <Table.Row
                {...row.getRowProps()}
                {...getRowInteractionRowProps(
                  { state: tableState, row },
                  {
                    onClick: (e) => {
                      logEvent("Jobs Page Job Clicked");

                      // TODO: Proper way is to use an a tag and href
                      // This causes issues with navigating when clicking on the selection column
                      if (e.metaKey || e.ctrlKey) {
                        const win = window.open(
                          `/jobs/${row.original.id}`,
                          "_blank"
                        );
                        win?.focus();
                      } else {
                        router.push(`/jobs/${row.original.id}`);
                      }
                    },
                  }
                )}
                className="h-16"
              >
                {row.cells.map((cell) => (
                  <Table.Cell {...cell.getCellProps()}>
                    {/* @ts-expect-error - react-table not properly typed in React 18 */}
                    {cell.render("Cell")}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
          {!rows.length && (
            <NoTableResults
              loading={loading}
              hasResults={hasResults}
              isFiltered={isFiltered}
              clearFilters={clearFilters}
            />
          )}
        </Table.Body>
      </Table.Root>
      <BulkActionBar
        isVisible={!!numSelectedRows}
        selectedCount={numSelectedRows}
        totalCount={totalCount ?? 0}
        itemDisplayText="job"
        isLoading={loading}
        onSelectAll={() => {
          logEvent("Jobs Table - Select All Jobs in BulkActionBar Clicked");
          toggleAll(true);
        }}
        onClearAll={() => toggleAll(false)}
        onClose={() => toggleAll(false)}
      >
        <ButtonGroup>
          {/* Disable bulk guide template assignment for transition users since a given job could be v1 OR v2 */}
          {/* Can revisit and implement, but will take some refactoring to handle the transition case */}
          <GuideTemplateAssignmentAction selectedIds={selectedIds} />
        </ButtonGroup>
      </BulkActionBar>
      {hasResults ? (
        <Pagination
          totalCount={totalJobsCount}
          pageSize={pageSize}
          pageIndex={pageIndex}
          isLoading={loading}
          onPrevPage={() => {
            logEvent("Jobs Table - Page Changed", {
              direction: "previous",
            });
            previousPage();
          }}
          onNextPage={() => {
            logEvent("Jobs Table - Page Changed", {
              direction: "next",
            });
            nextPage();
          }}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          onPageSizeChange={(newPageSize: number) => {
            logEvent("Jobs Table - Page Size Changed", {
              target: newPageSize,
              previousValue: pageSize,
            });
            setPageSize(newPageSize);
          }}
        />
      ) : null}
    </div>
  );
}

export default JobsTable;
